import React from 'react';
import { bool } from 'prop-types';
import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { propTypes } from '../../util/types';
import { resolveLatestProcessName, getProcess } from '../../transactions/transaction';

import css from './OrderBreakdown.module.css';

const LineItemTotalPrice = props => {
  const { transaction, isProvider, intl } = props;
  const processName = resolveLatestProcessName(transaction?.attributes?.processName);
  if (!processName) {
    return null;
  }
  const process = getProcess(processName);
  const isCompleted = process.isCompleted(transaction?.attributes?.lastTransition);
  const isRefunded = process.isRefunded(transaction?.attributes?.lastTransition);

  let providerTotalMessageId = 'OrderBreakdown.providerTotalDefault';
  if (isCompleted) {
    providerTotalMessageId = 'OrderBreakdown.providerTotalReceived';
  } else if (isRefunded) {
    providerTotalMessageId = 'OrderBreakdown.providerTotalRefunded';
  }

  const totalLabel = isProvider ? (
    <FormattedMessage id={providerTotalMessageId} />
  ) : (
    <FormattedMessage id="OrderBreakdown.total" />
  );

  const totalPrice = isProvider
    ? transaction.attributes.payoutTotal
    : transaction.attributes.payinTotal;
  const formattedTotalPrice = formatMoney(intl, totalPrice);

  const customerTotal = transaction?.attributes?.payinTotal?.amount / 100;
  const providerTotal = transaction?.attributes?.payoutTotal?.amount / 100;

  const calcNetVat = total => {
    const net = (total / 1.19);
    const vat = (total - net);
    return {
      net: net.toFixed(2),
      vat: vat.toFixed(2)
    };
  };

  const customerNetVat = calcNetVat(customerTotal);
  const providerNetVat = calcNetVat(providerTotal);

  const formatMoneyByLocale = (amount, locale, currency) => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      useGrouping: true
    }).format(amount);
  };

  const formattedCustomerNet_en = formatMoneyByLocale(customerNetVat.net, 'en-US', 'EUR');
  const formattedCustomerVat_en = formatMoneyByLocale(customerNetVat.vat, 'en-US', 'EUR');
  const formattedCustomerNet_de = formatMoneyByLocale(customerNetVat.net, 'de-DE', 'EUR');
  const formattedCustomerVat_de = formatMoneyByLocale(customerNetVat.vat, 'de-DE', 'EUR');

  const formattedProviderNet_en = formatMoneyByLocale(providerNetVat.net, 'en-US', 'EUR');
  const formattedProviderVat_en = formatMoneyByLocale(providerNetVat.vat, 'en-US', 'EUR');
  const formattedProviderNet_de = formatMoneyByLocale(providerNetVat.net, 'de-DE', 'EUR');
  const formattedProviderVat_de = formatMoneyByLocale(providerNetVat.vat, 'de-DE', 'EUR');

  return (
    <>
      <hr className={css.totalDivider} />
      <div className={css.totalVAT}>
        {isProvider ? (
          <FormattedMessage
            id="OrderBreakdown.providerVAT"
            values={{
              providerVat_en: formattedProviderVat_en,
              providerVat_de: formattedProviderVat_de,

              providerNet_en: formattedProviderNet_en,
              providerNet_de: formattedProviderNet_de,
            }}
          />
        ) : (
          <FormattedMessage
            id="OrderBreakdown.customerVAT"
            values={{
              customerVat_en: formattedCustomerVat_en,
              customerVat_de: formattedCustomerVat_de,

              customerNet_en: formattedCustomerNet_en,
              customerNet_de: formattedCustomerNet_de,
            }}
          />
        )}
      </div>
      <div className={css.lineItemTotal}>
        <div className={css.totalLabel}>{totalLabel}</div>
        <div className={css.totalPrice}>{formattedTotalPrice}</div>
      </div>
    </>
  );
};

LineItemTotalPrice.propTypes = {
  transaction: propTypes.transaction.isRequired,
  isProvider: bool.isRequired,
  intl: intlShape.isRequired,
};

export default LineItemTotalPrice;
